import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAgents, useChat, useModels } from "../hooks.js";
import { AgentSelector } from "./AgentSelector.js";
import { ModelSelector } from "./ModelSelector.js";
import { OptionSelector } from "./OptionSelector.js";
import { MessageList } from "./MessageList.js";
import { MessageInput } from "./MessageInput.js";
import { ensureStyles } from "../styles.js";
function formatReasoningEffort(effort) {
    if (effort === "xhigh")
        return "Extra high";
    if (effort === "max")
        return "Maximum";
    return effort.charAt(0).toUpperCase() + effort.slice(1);
}
function formatTokenCount(tokens) {
    if (!tokens)
        return undefined;
    if (tokens >= 1_000_000)
        return `${Number((tokens / 1_000_000).toFixed(1))}M tokens`;
    if (tokens >= 1_000)
        return `${Math.round(tokens / 1_000)}K tokens`;
    return `${tokens} tokens`;
}
export function ChatView({ baseUrl, getAuthToken, preferredAgent, defaultAgent, 
// `debug` is reserved for future debugging UI. Currently unused.
debug: _debug = false, starterQuestions: starterQuestionsProp, initialMessage, initialAgent, initialMessageKey, onInitialMessageConsumed, }) {
    const { agents, loading, error, refetch } = useAgents(baseUrl, getAuthToken);
    const { models, loading: modelsLoading, error: modelsError } = useModels(baseUrl, getAuthToken);
    const [selectedAgent, setSelectedAgent] = useState("");
    const [selectedModel, setSelectedModel] = useState("");
    const [reasoningEffort, setReasoningEffort] = useState("");
    const [contextTier, setContextTier] = useState("default");
    const lastInitialMessageRef = useRef(null);
    const pendingRouteRef = useRef(null);
    const [pendingSuggestedPrompt, setPendingSuggestedPrompt] = useState();
    const [queuedSuggestedPrompt, setQueuedSuggestedPrompt] = useState();
    useEffect(() => {
        ensureStyles();
    }, []);
    useEffect(() => {
        if (selectedAgent)
            return;
        const target = preferredAgent ?? defaultAgent;
        if (!target)
            return;
        if (!agents.some((a) => a.name === target))
            return;
        setSelectedAgent(target);
    }, [preferredAgent, defaultAgent, agents, selectedAgent]);
    const agentName = useMemo(() => selectedAgent
        || (preferredAgent && agents.some((a) => a.name === preferredAgent) ? preferredAgent : undefined)
        || (defaultAgent && agents.some((a) => a.name === defaultAgent) ? defaultAgent : undefined)
        || agents[0]?.name
        || "", [selectedAgent, preferredAgent, defaultAgent, agents]);
    const selectedAgentInfo = agents.find((agent) => agent.name === agentName);
    const agentDefaultModel = selectedAgentInfo?.model ?? "";
    const isCopilotSdkAgent = selectedAgentInfo?.runtime === "copilot-sdk";
    const resolvedDefaultModel = isCopilotSdkAgent
        ? models.find((model) => model.id === agentDefaultModel)?.id ?? models[0]?.id ?? agentDefaultModel
        : agentDefaultModel;
    const modelName = isCopilotSdkAgent
        ? models.length > 0
            ? models.find((model) => model.id === selectedModel)?.id ?? resolvedDefaultModel
            : agentDefaultModel
        : agentDefaultModel;
    const selectedModelInfo = models.find((model) => model.id === modelName);
    const reasoningOptions = (selectedModelInfo?.supportedReasoningEfforts ?? []).map((effort) => ({
        value: effort,
        label: formatReasoningEffort(effort),
    }));
    const defaultReasoningEffort = selectedAgentInfo?.reasoningEffort
        && reasoningOptions.some((option) => option.value === selectedAgentInfo.reasoningEffort)
        ? selectedAgentInfo.reasoningEffort
        : selectedModelInfo?.defaultReasoningEffort
            && reasoningOptions.some((option) => option.value === selectedModelInfo.defaultReasoningEffort)
            ? selectedModelInfo.defaultReasoningEffort
            : reasoningOptions[0]?.value ?? "";
    const contextOptions = selectedModelInfo?.longContextWindowTokens
        ? [
            {
                value: "default",
                label: "Standard",
                description: formatTokenCount(selectedModelInfo.contextWindowTokens),
            },
            {
                value: "long_context",
                label: "Long context",
                description: formatTokenCount(selectedModelInfo.longContextWindowTokens),
            },
        ]
        : [];
    const defaultContextTier = selectedAgentInfo?.contextTier === "long_context"
        && selectedModelInfo?.longContextWindowTokens
        ? "long_context"
        : "default";
    useEffect(() => {
        setSelectedModel(resolvedDefaultModel);
    }, [agentName, resolvedDefaultModel]);
    useEffect(() => {
        setReasoningEffort(defaultReasoningEffort);
        setContextTier(defaultContextTier);
    }, [agentName, defaultContextTier, defaultReasoningEffort, modelName]);
    const handleAgentRouted = useCallback((targetAgent, originalMessage) => {
        pendingRouteRef.current = { agent: targetAgent, message: originalMessage };
        const targetDefault = agents.find((agent) => agent.name === targetAgent)?.model ?? "";
        setSelectedModel(models.find((model) => model.id === targetDefault)?.id ?? models[0]?.id ?? "");
        setSelectedAgent(targetAgent);
    }, [agents, models]);
    const { messages, sending, runStatus, send, resolve, reset, hasPendingConfirmation, hasPendingInput } = useChat(baseUrl, agentName, getAuthToken, {
        model: modelName,
        reasoningEffort: reasoningEffort || undefined,
        contextTier: contextOptions.length > 0 ? contextTier : undefined,
        onAgentRouted: handleAgentRouted,
    });
    // Auto-forward message after concierge routing
    useEffect(() => {
        const pending = pendingRouteRef.current;
        if (!pending)
            return;
        if (agentName !== pending.agent)
            return;
        pendingRouteRef.current = null;
        send(pending.message);
    }, [agentName, send]);
    const inputDisabled = sending || hasPendingConfirmation || hasPendingInput || !agentName;
    const resolvedStarterQuestions = (preferredAgent && agentName === preferredAgent ? starterQuestionsProp : undefined) ??
        agents.find((a) => a.name === agentName)?.starterQuestions ??
        [];
    const showStarters = messages.length === 0 && resolvedStarterQuestions.length > 0 && !sending;
    const [inputPrefill, setInputPrefill] = useState();
    useEffect(() => {
        if (!initialMessage)
            return;
        if (!agentName)
            return;
        const requestKey = initialMessageKey ?? initialMessage;
        if (lastInitialMessageRef.current === requestKey)
            return;
        const preferredAvailable = !!preferredAgent && agents.some((a) => a.name === preferredAgent);
        const requestedAgent = initialAgent && agents.some((agent) => agent.name === initialAgent)
            ? initialAgent
            : preferredAvailable
                ? preferredAgent
                : agentName;
        if (!requestedAgent)
            return;
        lastInitialMessageRef.current = requestKey;
        if (initialAgent && messages.length > 0 && requestedAgent !== agentName) {
            setPendingSuggestedPrompt({ agent: requestedAgent, message: initialMessage });
        }
        else if (initialAgent && messages.length > 0) {
            send(initialMessage);
        }
        else if (initialAgent) {
            setSelectedAgent(requestedAgent);
            setQueuedSuggestedPrompt({ agent: requestedAgent, message: initialMessage });
        }
        else if (messages.length > 0) {
            setInputPrefill(initialMessage);
        }
        else {
            send(initialMessage);
        }
        onInitialMessageConsumed?.();
    }, [agentName, agents, initialAgent, initialMessage, initialMessageKey, messages.length, onInitialMessageConsumed, preferredAgent, send]);
    useEffect(() => {
        if (!queuedSuggestedPrompt)
            return;
        if (agentName !== queuedSuggestedPrompt.agent || messages.length > 0 || sending)
            return;
        if (selectedModel !== resolvedDefaultModel
            || reasoningEffort !== defaultReasoningEffort
            || contextTier !== defaultContextTier)
            return;
        setQueuedSuggestedPrompt(undefined);
        send(queuedSuggestedPrompt.message);
    }, [agentName, contextTier, defaultContextTier, defaultReasoningEffort, messages.length,
        queuedSuggestedPrompt, reasoningEffort, resolvedDefaultModel, selectedModel, send, sending]);
    const startSuggestedPrompt = () => {
        if (!pendingSuggestedPrompt)
            return;
        const targetAgent = agents.find((agent) => agent.name === pendingSuggestedPrompt.agent);
        const targetModel = targetAgent?.model ?? "";
        reset();
        setInputPrefill(undefined);
        setSelectedModel(models.find((model) => model.id === targetModel)?.id ?? models[0]?.id ?? targetModel);
        setReasoningEffort("");
        setContextTier("default");
        setSelectedAgent(pendingSuggestedPrompt.agent);
        setQueuedSuggestedPrompt(pendingSuggestedPrompt);
        setPendingSuggestedPrompt(undefined);
    };
    if (loading) {
        return _jsx("div", { className: "loading", children: "Loading agents\u2026" });
    }
    if (error) {
        return (_jsxs("div", { className: "error-panel", children: [_jsx("h2", { children: "Connection Error" }), _jsx("p", { children: error }), _jsx("p", { children: "Make sure the backend is running and you have a valid auth token set." }), _jsx("button", { className: "btn-retry", onClick: refetch, type: "button", children: "\uD83D\uDD04 Retry" })] }));
    }
    if (agents.length === 0) {
        return (_jsxs("div", { className: "error-panel", children: [_jsx("h2", { children: "No Agents Available" }), _jsx("p", { children: "The server returned no agents. Register agents on the backend." })] }));
    }
    return (_jsxs("div", { className: "copilot-agents-root chat-view", children: [_jsxs("header", { className: "toolbar", children: [_jsx(AgentSelector, { agents: agents, selected: agentName, onChange: (name) => {
                            const targetDefault = agents.find((agent) => agent.name === name)?.model ?? "";
                            setSelectedModel(models.find((model) => model.id === targetDefault)?.id ?? models[0]?.id ?? "");
                            setSelectedAgent(name);
                            reset();
                        }, disabled: sending }), _jsx(ModelSelector, { models: models, selected: modelName, onChange: setSelectedModel, disabled: sending || !isCopilotSdkAgent || modelsLoading || !!modelsError || models.length === 0, loading: modelsLoading, error: modelsError }), _jsxs("div", { className: "toolbar-action-row", children: [reasoningOptions.length > 0 && (_jsx(OptionSelector, { label: "Thinking", options: reasoningOptions, selected: reasoningEffort, onChange: (value) => setReasoningEffort(value), disabled: sending })), contextOptions.length > 0 && (_jsx(OptionSelector, { label: "Context", options: contextOptions, selected: contextTier, onChange: (value) => setContextTier(value), disabled: sending })), _jsx("button", { className: "btn-new", onClick: reset, disabled: sending, type: "button", children: "\uD83D\uDDD2\uFE0F New Chat" })] }), selectedAgentInfo?.description && (_jsx("div", { className: "toolbar-description", children: selectedAgentInfo.description }))] }), _jsx(MessageList, { messages: messages, runStatus: runStatus, onResolve: (messageId, runId, approvals, answers) => void resolve(messageId, runId, approvals, answers) }), showStarters && (_jsx("div", { className: "chat-starters", children: resolvedStarterQuestions.map((q) => (_jsx("button", { className: "chat-starter", type: "button", onClick: () => send(q), children: q }, q))) })), _jsx(MessageInput, { onSend: send, disabled: inputDisabled, prefill: inputPrefill }), _jsx("div", { className: "ai-disclaimer", children: "AI-generated content may be incorrect" }), pendingSuggestedPrompt && (_jsx("div", { className: "new-chat-prompt-backdrop", role: "presentation", onClick: () => setPendingSuggestedPrompt(undefined), children: _jsxs("div", { "aria-describedby": "new-chat-prompt-description", "aria-labelledby": "new-chat-prompt-title", "aria-modal": "true", className: "new-chat-prompt", onClick: (event) => event.stopPropagation(), role: "dialog", children: [_jsx("h2", { id: "new-chat-prompt-title", children: "Start a new chat?" }), _jsxs("p", { id: "new-chat-prompt-description", children: ["This suggestion uses the ", _jsx("strong", { children: pendingSuggestedPrompt.agent }), " agent. Starting it will replace your active chat and use the agent defaults."] }), _jsx("blockquote", { children: pendingSuggestedPrompt.message }), _jsxs("div", { className: "new-chat-prompt-actions", children: [_jsx("button", { className: "new-chat-prompt-cancel", onClick: () => setPendingSuggestedPrompt(undefined), type: "button", children: "Cancel" }), _jsx("button", { autoFocus: true, className: "new-chat-prompt-confirm", onClick: startSuggestedPrompt, type: "button", children: "Start new chat" })] })] }) }))] }));
}
//# sourceMappingURL=ChatView.js.map